/**
 * Business Processes admin page (SmartImprove).
 *
 * Single, consulting-friendly screen that lists every canonical Business
 * Process and lets the admin expand each row to manage its Sub-Processes
 * inline. NO separate sub-process page, NO extra left-nav item.
 *
 * Backend collections (`smartimprove_operational_areas` +
 * `smartimprove_sub_processes`) are unchanged; user-facing names are
 * "Business Process" / "Sub-Process". Technical `key` columns are hidden
 * from admins — keys are auto-derived from the Name on create.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  operationalAreasApi,
  subProcessesApi,
} from "../../lib/smartimproveAdminApi";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function slugify(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

const EMPTY_BP = { id: null, key: "", label: "", short_code: "", description: "", sort_order: 0, active: true };
const EMPTY_SP = { id: null, key: "", label: "", description: "", process_key: "", sort_order: 0, active: true };

// ---------------------------------------------------------------------------
// Business Process row (accordion)
// ---------------------------------------------------------------------------
function ProcessRow({
  process,
  subProcesses,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onAddSub,
  onEditSub,
  onDeleteSub,
}) {
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const subs = useMemo(
    () =>
      (subProcesses || [])
        .filter((s) => s.process_key === process.key)
        .sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
            String(a.label).localeCompare(String(b.label))
        ),
    [subProcesses, process.key]
  );
  const activeCount = subs.filter((s) => s.active !== false).length;

  return (
    <div
      className="border border-[#E5E7EB] rounded-sm bg-white"
      data-testid={`bp-row-${process.id}`}
    >
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-[#F8FAFC]"
        onClick={onToggle}
        data-testid={`bp-row-header-${process.id}`}
      >
        <button
          type="button"
          aria-label={expanded ? "Collapse" : "Expand"}
          className="mt-0.5 text-[#6B7280] hover:text-[#025979]"
          data-testid={`bp-toggle-${process.id}`}
        >
          <Chevron className="w-4 h-4" strokeWidth={2} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {process.short_code ? (
              <span
                className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-sm bg-[#E6F4FA] text-[#025979] border border-[#B5DEF0] font-mono"
                data-testid={`bp-short-code-${process.id}`}
                title="Short code — drives finding IDs (e.g. INB01)"
              >
                {process.short_code}
              </span>
            ) : null}
            <h3
              className="text-sm font-semibold text-[#111827] truncate"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {process.label}
            </h3>
            <span
              className="text-[10px] text-[#6B7280]"
              data-testid={`bp-subcount-${process.id}`}
            >
              · {activeCount} sub-process{activeCount === 1 ? "" : "es"}
            </span>
          </div>
          {process.description ? (
            <p className="text-xs text-[#6B7280] mt-0.5 line-clamp-1">
              {process.description}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {process.active === false ? (
            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
              inactive
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]">
              active
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(process);
            }}
            className="text-xs font-semibold text-[#025979] hover:text-[#03738C]"
            data-testid={`bp-edit-${process.id}`}
            aria-label={`Edit ${process.label}`}
          >
            <Pencil className="inline w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(process);
            }}
            className="text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D]"
            data-testid={`bp-delete-${process.id}`}
            aria-label={`Delete ${process.label}`}
          >
            <Trash2 className="inline w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {expanded ? (
        <div
          className="border-t border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3"
          data-testid={`bp-expanded-${process.id}`}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#6B7280]">
              Sub-Processes
            </p>
            <Button
              size="sm"
              onClick={() => onAddSub(process)}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-[11px] h-7"
              data-testid={`bp-add-sub-${process.id}`}
            >
              <Plus className="w-3 h-3 mr-1" /> Add Sub-Process
            </Button>
          </div>
          {subs.length === 0 ? (
            <p
              className="text-xs text-[#6B7280] py-3 text-center"
              data-testid={`bp-no-subs-${process.id}`}
            >
              No sub-processes yet. Click <strong>Add Sub-Process</strong> to create one.
            </p>
          ) : (
            <ul className="divide-y divide-[#E5E7EB] bg-white border border-[#E5E7EB] rounded-sm">
              {subs.map((sp) => (
                <li
                  key={sp.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-[#F8FAFC]"
                  data-testid={`sp-row-${sp.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-[#111827]">
                        {sp.label}
                      </span>
                      {sp.active === false ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
                          inactive
                        </span>
                      ) : null}
                    </div>
                    {sp.description ? (
                      <p className="text-[11px] text-[#6B7280] mt-0.5 line-clamp-1">
                        {sp.description}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => onEditSub(sp)}
                    className="text-xs font-semibold text-[#025979] hover:text-[#03738C]"
                    data-testid={`sp-edit-${sp.id}`}
                    aria-label={`Edit ${sp.label}`}
                  >
                    <Pencil className="inline w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteSub(sp)}
                    className="text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D]"
                    data-testid={`sp-delete-${sp.id}`}
                    aria-label={`Delete ${sp.label}`}
                  >
                    <Trash2 className="inline w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal forms
// ---------------------------------------------------------------------------
function ProcessDialog({ open, onOpenChange, value, onChange, onSave, saving }) {
  const isEdit = !!value?.id;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bp-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Business Process" : "New Business Process"}</DialogTitle>
          <DialogDescription>
            Canonical warehouse process used across SmartImprove, SmartEvaluate alignment, recommendations, KPIs, and insights.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">Name</label>
            <Input
              value={value?.label || ""}
              onChange={(e) => onChange({ ...value, label: e.target.value })}
              placeholder="e.g. Inbound Processes"
              className="rounded-sm"
              data-testid="bp-field-label"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">Description</label>
            <Textarea
              value={value?.description || ""}
              onChange={(e) => onChange({ ...value, description: e.target.value })}
              rows={3}
              className="rounded-sm"
              data-testid="bp-field-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1">
                Short code <span className="text-[10px] text-[#6B7280]">(2-5 chars · drives finding IDs like INB01)</span>
              </label>
              <Input
                value={value?.short_code || ""}
                onChange={(e) =>
                  onChange({
                    ...value,
                    // Auto-uppercase + strip non-alphanumeric as the
                    // admin types. Backend enforces the same rules + a
                    // uniqueness check across active Business Processes.
                    short_code: e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 5),
                  })
                }
                placeholder="INB, OUT, PRD…"
                className="rounded-sm font-mono uppercase tracking-wider"
                maxLength={5}
                data-testid="bp-field-short-code"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1">Sort order</label>
              <Input
                type="number"
                value={value?.sort_order ?? 0}
                onChange={(e) =>
                  onChange({ ...value, sort_order: e.target.value })
                }
                className="rounded-sm"
                data-testid="bp-field-sort-order"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="bp-active"
              type="checkbox"
              checked={value?.active !== false}
              onChange={(e) =>
                onChange({ ...value, active: e.target.checked })
              }
              data-testid="bp-field-active"
            />
            <label htmlFor="bp-active" className="text-xs text-[#374151]">
              Active
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="bp-cancel"
            className="rounded-sm text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !value?.label?.trim()}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="bp-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubProcessDialog({
  open,
  onOpenChange,
  value,
  processes,
  onChange,
  onSave,
  saving,
}) {
  const isEdit = !!value?.id;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sp-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Sub-Process" : "New Sub-Process"}</DialogTitle>
          <DialogDescription>
            Sub-processes live under a parent Business Process and surface in
            consultant intake, insights, and recommendations.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">Name</label>
            <Input
              value={value?.label || ""}
              onChange={(e) => onChange({ ...value, label: e.target.value })}
              placeholder="e.g. Receiving"
              className="rounded-sm"
              data-testid="sp-field-label"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Parent Business Process
            </label>
            <Select
              value={value?.process_key || ""}
              onValueChange={(v) => onChange({ ...value, process_key: v })}
            >
              <SelectTrigger className="rounded-sm" data-testid="sp-field-parent">
                <SelectValue placeholder="Select parent business process" />
              </SelectTrigger>
              <SelectContent>
                {processes
                  .filter((p) => p.active !== false)
                  .map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">Description</label>
            <Textarea
              value={value?.description || ""}
              onChange={(e) => onChange({ ...value, description: e.target.value })}
              rows={3}
              className="rounded-sm"
              data-testid="sp-field-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1">Sort order</label>
              <Input
                type="number"
                value={value?.sort_order ?? 0}
                onChange={(e) =>
                  onChange({ ...value, sort_order: e.target.value })
                }
                className="rounded-sm"
                data-testid="sp-field-sort-order"
              />
            </div>
            <div className="flex items-center gap-2 mt-5">
              <input
                id="sp-active"
                type="checkbox"
                checked={value?.active !== false}
                onChange={(e) =>
                  onChange({ ...value, active: e.target.checked })
                }
                data-testid="sp-field-active"
              />
              <label htmlFor="sp-active" className="text-xs text-[#374151]">
                Active
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="sp-cancel"
            className="rounded-sm text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving || !value?.label?.trim() || !value?.process_key
            }
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="sp-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function BusinessProcessesPage() {
  const [processes, setProcesses] = useState([]);
  const [subProcesses, setSubProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [bpDialog, setBpDialog] = useState(false);
  const [bpForm, setBpForm] = useState(EMPTY_BP);
  const [spDialog, setSpDialog] = useState(false);
  const [spForm, setSpForm] = useState(EMPTY_SP);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [bps, sps] = await Promise.all([
        operationalAreasApi.list(),
        subProcessesApi.list(),
      ]);
      setProcesses(bps || []);
      setSubProcesses(sps || []);
    } catch (e) {
      toast.error("Failed to load business processes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const sortedProcesses = useMemo(
    () =>
      [...processes].sort(
        (a, b) =>
          (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
          String(a.label).localeCompare(String(b.label))
      ),
    [processes]
  );

  // --- Business Process actions ---
  const openCreateBp = () => {
    setBpForm({ ...EMPTY_BP });
    setBpDialog(true);
  };
  const openEditBp = (row) => {
    setBpForm({
      id: row.id,
      key: row.key,
      label: row.label,
      short_code: row.short_code || "",
      description: row.description || "",
      sort_order: row.sort_order ?? 0,
      active: row.active !== false,
    });
    setBpDialog(true);
  };
  const saveBp = async () => {
    const isEdit = !!bpForm.id;
    const label = (bpForm.label || "").trim();
    if (!label) return;
    // Auto-derive key from name on create; preserve existing key on edit so
    // historical records (issues / KPIs referencing operational_area=key) stay
    // valid.
    const key = isEdit ? bpForm.key : slugify(label);
    if (!key) {
      toast.error("Name must contain at least one letter or digit");
      return;
    }
    const payload = {
      key,
      label,
      short_code: (bpForm.short_code || "").trim().toUpperCase(),
      description: (bpForm.description || "").trim(),
      sort_order: Number(bpForm.sort_order) || 0,
      active: !!bpForm.active,
    };
    setSaving(true);
    try {
      if (isEdit) await operationalAreasApi.update(bpForm.id, payload);
      else await operationalAreasApi.create(payload);
      toast.success(`Business process ${isEdit ? "updated" : "created"}`);
      setBpDialog(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };
  const deleteBp = async (row) => {
    const hasSubs = subProcesses.some(
      (s) => s.process_key === row.key && s.active !== false
    );
    const msg = hasSubs
      ? `Delete "${row.label}" and its sub-processes will be orphaned. Continue?`
      : `Delete "${row.label}"?`;
    if (!window.confirm(msg)) return;
    try {
      await operationalAreasApi.remove(row.id);
      toast.success("Business process deleted");
      await reload();
    } catch (e) {
      toast.error("Failed to delete business process");
    }
  };

  // --- Sub-process actions ---
  const openCreateSp = (process) => {
    setSpForm({ ...EMPTY_SP, process_key: process.key });
    setSpDialog(true);
  };
  const openEditSp = (sp) => {
    setSpForm({
      id: sp.id,
      key: sp.key,
      label: sp.label,
      description: sp.description || "",
      process_key: sp.process_key,
      sort_order: sp.sort_order ?? 0,
      active: sp.active !== false,
    });
    setSpDialog(true);
  };
  const saveSp = async () => {
    const isEdit = !!spForm.id;
    const label = (spForm.label || "").trim();
    const process_key = spForm.process_key;
    if (!label || !process_key) return;
    const key = isEdit ? spForm.key : `${process_key}_${slugify(label)}`;
    if (!key) {
      toast.error("Name must contain at least one letter or digit");
      return;
    }
    const payload = {
      key,
      label,
      description: (spForm.description || "").trim(),
      process_key,
      sort_order: Number(spForm.sort_order) || 0,
      active: !!spForm.active,
    };
    setSaving(true);
    try {
      if (isEdit) await subProcessesApi.update(spForm.id, payload);
      else await subProcessesApi.create(payload);
      toast.success(`Sub-process ${isEdit ? "updated" : "created"}`);
      setSpDialog(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };
  const deleteSp = async (sp) => {
    if (!window.confirm(`Delete sub-process "${sp.label}"?`)) return;
    try {
      await subProcessesApi.remove(sp.id);
      toast.success("Sub-process deleted");
      await reload();
    } catch (e) {
      toast.error("Failed to delete sub-process");
    }
  };

  return (
    <div className="space-y-5" data-testid="si-operational-areas">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Configuration
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Business Processes
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            Canonical warehouse business processes used across SmartImprove,
            SmartEvaluate alignment, recommendations, KPIs, insights, and
            future FutuRoot analytics. Expand any row to manage its
            sub-processes inline.
          </p>
        </div>
        <Button
          onClick={openCreateBp}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="si-operational-areas-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Business Process
        </Button>
      </header>

      <section className="space-y-2">
        {loading ? (
          <div className="px-5 py-8 text-sm text-[#6B7280]">Loading…</div>
        ) : sortedProcesses.length === 0 ? (
          <div
            className="px-5 py-12 text-center text-sm text-[#6B7280] bg-white border border-[#E5E7EB] rounded-sm"
            data-testid="si-operational-areas-empty"
          >
            No business processes yet. Click <strong>Add Business Process</strong>{" "}
            to create the first one.
          </div>
        ) : (
          sortedProcesses.map((p) => (
            <ProcessRow
              key={p.id}
              process={p}
              subProcesses={subProcesses}
              expanded={!!expanded[p.id]}
              onToggle={() =>
                setExpanded((m) => ({ ...m, [p.id]: !m[p.id] }))
              }
              onEdit={openEditBp}
              onDelete={deleteBp}
              onAddSub={openCreateSp}
              onEditSub={openEditSp}
              onDeleteSub={deleteSp}
            />
          ))
        )}
      </section>

      <ProcessDialog
        open={bpDialog}
        onOpenChange={setBpDialog}
        value={bpForm}
        onChange={setBpForm}
        onSave={saveBp}
        saving={saving}
      />
      <SubProcessDialog
        open={spDialog}
        onOpenChange={setSpDialog}
        value={spForm}
        processes={sortedProcesses}
        onChange={setSpForm}
        onSave={saveSp}
        saving={saving}
      />
    </div>
  );
}
